/**
 * Progress model — pure and fully tested.
 *
 * Progress must be EARNED where possible and CHEAP where not:
 * - A task with an action plan derives progress from its steps (done = 1,
 *   started = ½). Automatic, honest, zero extra effort.
 * - A stepless task can be marked in-progress with one tap; it shows a
 *   fixed partial fill — a truthful "underway", not a fake percentage.
 * - Zone progress is the mean of task progress, so a zone visibly fills as
 *   the week advances — the sense-of-accomplishment view.
 */

import type { ActionStep, Task } from '../types';

/** Visual fill for a started-but-stepless task ("underway", not a %). */
export const STARTED_FILL = 0.3;

export function stepProgress(step: ActionStep): number {
  if (step.done) return 1;
  return step.startedAt ? 0.5 : 0;
}

/** 0..1 — done tasks are 1 regardless of their plan's state. */
export function taskProgress(task: Task): number {
  if (task.done) return 1;
  if (task.steps.length > 0) {
    const sum = task.steps.reduce((acc, st) => acc + stepProgress(st), 0);
    return sum / task.steps.length;
  }
  return task.startedAt ? STARTED_FILL : 0;
}

/** Mean task progress for a zone (0 when empty). */
export function zoneProgress(tasks: Task[]): number {
  if (tasks.length === 0) return 0;
  return tasks.reduce((acc, t) => acc + taskProgress(t), 0) / tasks.length;
}

/** All steps complete but the task itself not yet marked done — the moment
 *  to offer "mark complete?" (the user chooses; the app never auto-closes). */
export function readyToComplete(task: Task): boolean {
  return !task.done && task.steps.length > 0 && task.steps.every((st) => st.done);
}
